import { useCallback, useEffect, useState } from 'react';
import { useMultipartUpload } from 'services/file-manager';

export interface AttachmentWithPreview {
	id: string;
	file: File;
	preview?: string;
}

export const useAttachments = () => {
	const [attachments, setAttachments] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const { uploadStatus, uploadFiles, resetStatus, removeUploadStatusByFileName } = useMultipartUpload();

	// Add files to attachments and start upload
	const addAttachments = useCallback(
		async (files: File[]) => {
			if (files.length === 0) return;

			setAttachments((prev) => [...prev, ...files]);
			setIsUploading(true);

			try {
				await uploadFiles(files);
			} catch (error) {
				console.error('Failed to upload files:', error);
			}
		},
		[uploadFiles]
	);

	// Remove attachment by index and clean up corresponding upload status
	const removeAttachment = useCallback((index: number) => {
		setAttachments((prev) => {
			const fileToRemove = prev[index];
			if (fileToRemove) {
				// Remove the corresponding upload status by filename
				removeUploadStatusByFileName(fileToRemove.name);
			}
			return prev.filter((_, i) => i !== index);
		});
	}, [removeUploadStatusByFileName]);

	// Clear all attachments
	const clearAttachments = useCallback(() => {
		setAttachments([]);
		resetStatus();
	}, [resetStatus]);

	// Create attachment objects for sending
	const getAttachmentObjects = useCallback(() => {
		return uploadStatus
			.filter((status) => status.isCompleted && status.key)
			.filter((status) => attachments.some((file) => file.name === status.fileName))
			.map((status) => {
				const matchingFile = attachments.find((file) => file.name === status.fileName);
				return {
					fileName: status.fileName,
					s3_key: status.key,
					fileType: matchingFile ? matchingFile.type : 'application/octet-stream',
					thumbnailKey: '',
				};
			});
	}, [uploadStatus, attachments]);

	// Check if all attachments are uploaded
	const areAllUploaded = useCallback(() => {
		return (
			attachments.length > 0 &&
			uploadStatus.length > 0 &&
			uploadStatus.every((status) => status.isCompleted && !status.error) &&
			attachments.every((file) =>
				uploadStatus.some(
					(status) => status.fileName === file.name && status.isCompleted && !status.error
				)
			)
		);
	}, [attachments, uploadStatus]);

	// Monitor upload status and sync with attachments
	useEffect(() => {
		if (uploadStatus.length > 0) {
			const hasInProgressUploads = uploadStatus.some(
				(status) => !status.isCompleted && !status.error
			);
			setIsUploading(hasInProgressUploads);
		} else {
			setIsUploading(false);
		}
	}, [uploadStatus]);

	// Clean up orphaned upload statuses that don't have corresponding attachments
	useEffect(() => {
		const attachmentFileNames = new Set(attachments.map(file => file.name));
		const orphanedStatuses = uploadStatus.filter(status => 
			!attachmentFileNames.has(status.fileName) && status.isCompleted
		);
		
		orphanedStatuses.forEach(status => {
			if (status.fileName) {
				removeUploadStatusByFileName(status.fileName);
			}
		});
	}, [attachments, uploadStatus, removeUploadStatusByFileName]);

	return {
		attachments,
		uploadStatus,
		isUploading,
		addAttachments,
		removeAttachment,
		clearAttachments,
		getAttachmentObjects,
		areAllUploaded,
	};
};
